import type { ModuleDef } from '@pmai/contracts';
import { Button, EmptyState, PageHeader } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDocumentTitle } from './use-document-title';

/** A registry module that is not migrated yet (status `planned`) — the route exists, the page says so. */
export function PlannedPage({ module }: { module: ModuleDef }) {
  const { t } = useTranslation();
  const label = t(module.labelKey);
  return (
    <>
      <PageHeader title={label} />
      <EmptyState
        icon={module.icon}
        title={t('shell.plannedTitle')}
        description={t('shell.plannedText', { module: label })}
      />
    </>
  );
}

function GoHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Button variant="secondary" onClick={() => void navigate('/')}>
      {t('shell.goHome')}
    </Button>
  );
}

/** FR-WS-008: a page the role may not open (direct URL). */
export function ForbiddenPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('shell.forbiddenTitle'));
  return (
    <EmptyState
      icon="key-round"
      title={t('shell.forbiddenTitle')}
      description={t('shell.forbiddenText')}
      action={<GoHome />}
    />
  );
}

export function NotFoundPage({ title, description }: { title?: string; description?: string }) {
  const { t } = useTranslation();
  const heading = title ?? t('shell.notFoundTitle');
  useDocumentTitle(heading);
  return (
    <EmptyState
      icon="search"
      title={heading}
      description={description ?? t('shell.notFoundText')}
      action={<GoHome />}
    />
  );
}

/** Signed in, but not a member of any workspace (e.g. removed). */
export function NoWorkspacePage() {
  const { t } = useTranslation();
  useDocumentTitle(t('shell.noWorkspaceTitle'));
  return (
    <EmptyState icon="users" title={t('shell.noWorkspaceTitle')} description={t('shell.noWorkspaceText')} />
  );
}
